/**
 * The virtual account's Ledger tab (TMMAPP-0108; audit 06 § A8, § Overrides 2, the tmm-api review
 * amendments; v2 L325–383, reference v2/01): `GET /v1/virtual-accounts/{id}/register`, one row per
 * bank line published into the virtual account, newest first, with the running balance.
 *
 * - The card header: "Ledger", its sub-line, and "{n of }{N} lines" from the served `totals.count`
 *   and `allCount` (never counted here).
 * - Search (`q`, debounced 300 ms) and the segmented All lines / Unassigned / Uncategorized filter
 *   (`unassigned=true` / `uncategorized=true`) are server-side and live in the URL; a filtered view
 *   shows the served `totals.netMinor` ("{±x} not in any envelope" / "{±x} without a category").
 *   Every change is announced politely.
 * - Columns Date · Description (+ "{account} ···· {last4}") · Category · Envelope · Amount · Balance
 *   at a 940px minimum, scrolling sideways below it. Each part's pill carries its envelope (or
 *   Unassigned) and its own category (§ Overrides 2).
 * - A row click reads the served `account.viewerOwns`: the line's owner chooses Change allocation
 *   (0109's allocate dialog, which changes the total) or Re-split (C7); a writer who isn't the owner
 *   gets C7; anyone else gets the owner snackbar.
 * - "Load older lines" (D1) follows the cursor; the empty states say whether a search or filter hid the lines.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import {
  Box,
  Button,
  InputAdornment,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { accountsApi, type Account } from '@/api/accounts';
import { categoriesApi, type Category } from '@/api/categories';
import type { Envelope } from '@/api/envelopes';
import {
  registersApi,
  type VirtualAccountRegisterLine,
  type VirtualAccountRegisterPage,
} from '@/api/registers';
import { transactionsApi, type OwnerRegisterLine } from '@/api/transactions';
import type { VirtualAccountDetail } from '@/api/virtualAccounts';
import { ErrorState } from '@/components/ErrorState';
import { SearchIcon } from '@/components/icons';
import { LiveRegion } from '@/components/LiveRegion';
import { MoneyText } from '@/components/MoneyText';
import { SegmentedControl } from '@/components/SegmentedControl';
import { TabularText } from '@/components/TabularText';
import { isOwnerAccount } from '@/features/accounts/accountAccess';
import { AllocateDialog } from '@/features/accounts/AllocateDialog';
import { OwnedEnvelopesProvider } from '@/features/accounts/OwnedEnvelopesProvider';
import { formatCivilDate, formatMoneyMinor } from '@/i18n/format';
import { personName } from '@/i18n/personName';
import { useLocale } from '@/i18n/useLocale';
import { useDebouncedText } from '@/shared/useDebouncedText';
import { useUrlState } from '@/shared/useUrlState';
import { ObjectMenu } from '@/shell/OverflowMenu';
import { resolveTokens } from '@/theme/theme';
import { CategorySplitDialog } from './CategorySplitDialog';
import { CategoryTag, PartPill } from './ledgerParts';
import { lineCategories } from './lineCategories';
import { LEDGER_FILTERS, LEDGER_URL, type LedgerFilter } from './ledgerUrl';

/** v2's grid: 64 / 1fr / ≤190 / ≤220 / 104 / 112, at a 940px minimum (audit 06 § A8). */
const COLUMNS = [64, undefined, 190, 220, 104, 112] as const;
const DATE_SHORT = { month: 'short', day: 'numeric' } as const;

interface LedgerTabProps {
  virtualAccount: VirtualAccountDetail;
  /** The active envelopes, for the re-split's destinations. */
  envelopes: Envelope[];
  /** A writer: the owner before TMMAPP-0110. */
  canWrite: boolean;
  onNotify: (message: string) => void;
  /** An allocation or a re-split changed the figures: the page reloads the detail. */
  onChanged: () => void | Promise<void>;
  onSubscriptionRequired: () => void;
}

type Dialog =
  | { kind: 'choice'; line: VirtualAccountRegisterLine; anchor: HTMLElement }
  | { kind: 'allocate'; account: Account; line: OwnerRegisterLine }
  | { kind: 'split'; line: VirtualAccountRegisterLine }
  | null;

export function LedgerTab({
  virtualAccount,
  envelopes,
  canWrite,
  onNotify,
  onChanged,
  onSubscriptionRequired,
}: LedgerTabProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const [{ q, filter }, setUrl] = useUrlState(LEDGER_URL);
  const [search, setSearch] = useDebouncedText(q, (value) => setUrl({ q: value.trim() }));
  const [tick, setTick] = useState(0);
  const key = `${virtualAccount.id}|${q}|${filter}|${tick}`;
  const [state, setState] = useState<{ key: string; page: VirtualAccountRegisterPage } | null>(
    null,
  );
  const [failure, setFailure] = useState<{ key: string; error: unknown } | null>(null);
  const [more, setMore] = useState<{ busy: boolean; failed: boolean }>({
    busy: false,
    failed: false,
  });
  const [categories, setCategories] = useState<Category[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [dialog, setDialog] = useState<Dialog>(null);
  const seq = useRef(0);

  const query = useCallback(
    (cursor?: string) =>
      registersApi.virtualAccountRegister(virtualAccount.id, {
        q,
        unassigned: filter === 'unassigned',
        uncategorized: filter === 'uncategorized',
        ...(cursor !== undefined ? { cursor } : {}),
      }),
    [virtualAccount.id, q, filter],
  );

  useEffect(() => {
    const mine = ++seq.current;
    void query()
      .then((page) => {
        if (seq.current !== mine) return;
        setState({ key, page });
        setFailure(null);
      })
      .catch((error: unknown) => {
        if (seq.current === mine) setFailure({ key, error });
      });
  }, [query, key]);

  const loadCategories = useCallback(async () => {
    try {
      setCategories(await categoriesApi.list(virtualAccount.id));
    } catch {
      /* the pills read "—" for a name they can't resolve */
    }
  }, [virtualAccount.id]);

  const categoriesStarted = useRef(false);
  useEffect(() => {
    if (categoriesStarted.current) return;
    categoriesStarted.current = true;
    void loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    let live = true;
    void accountsApi
      .list()
      .then((rows) => {
        if (live) setAccounts(rows.filter(isOwnerAccount));
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  const page = state?.key === key ? state.page : null;
  const failed = failure?.key === key ? failure : null;
  const rows = page?.items ?? [];
  const totals = page?.totals ?? null;
  const filtered = q.length > 0 || filter !== 'all';
  const categoryName = (id: number | null) =>
    id === null ? null : (categories.find((category) => category.id === id)?.name ?? null);
  const money = (minor: number) => formatMoneyMinor(minor, virtualAccount.currency, locale, true);

  async function loadMore() {
    if (!page?.nextCursor || more.busy) return;
    setMore({ busy: true, failed: false });
    const mine = seq.current;
    try {
      const next = await query(page.nextCursor);
      if (seq.current !== mine) return;
      setState((prev) =>
        prev?.key === key
          ? {
              key,
              page: {
                items: [...prev.page.items, ...next.items],
                nextCursor: next.nextCursor,
                totals: prev.page.totals,
              },
            }
          : prev,
      );
      setMore({ busy: false, failed: false });
    } catch {
      setMore({ busy: false, failed: true });
    }
  }

  /** Reload the first page and answer `id` as it is now (the re-split's stale path). */
  async function refetchLine(id: number): Promise<VirtualAccountRegisterLine | null> {
    const fresh = await query();
    setState({ key, page: fresh });
    return fresh.items.find((row) => row.id === id) ?? null;
  }

  function ownedAccount(line: VirtualAccountRegisterLine): Account | undefined {
    if (line.account === null) return undefined;
    return accounts.find((account) => account.id === line.account?.id);
  }

  // A contribution (MM-D-120): the served `contributed` (TMMAPI-0152) reads under its contributor's
  // served name (`personName`), unless the line is the viewer's own.
  function contributionLabel(
    source: string,
    line: VirtualAccountRegisterLine,
    account: NonNullable<VirtualAccountRegisterLine['account']>,
  ): string {
    if (!line.contributed || account.viewerOwns === true) return source;
    return t('virtualAccount.ledger.contributedBy', {
      source,
      person: personName(account.ownerDisplayName, t),
    });
  }

  function owns(line: VirtualAccountRegisterLine): boolean {
    const served = line.account?.viewerOwns;
    return served ?? ownedAccount(line) !== undefined;
  }

  function open(line: VirtualAccountRegisterLine, anchor: HTMLElement) {
    const owner = owns(line) && line.account !== null;
    if (owner && canWrite) {
      setDialog({ kind: 'choice', line, anchor });
    } else if (owner) {
      // The line's owner who can't write in this virtual account (an archived one, or a member
      // before TMMAPP-0110): allocating is theirs, re-splitting isn't.
      void openAllocate(line);
    } else if (canWrite) {
      setDialog({ kind: 'split', line });
    } else {
      onNotify(
        t('virtualAccount.ledger.ownerOnly', {
          owner: personName(line.account?.ownerDisplayName ?? null, t),
          account: line.account?.name ?? '',
        }),
      );
    }
  }

  async function openAllocate(line: VirtualAccountRegisterLine) {
    if (line.account === null) return;
    try {
      // The served `viewerOwns` decides; the account itself may not be listed yet.
      const account = ownedAccount(line) ?? (await accountsApi.get(line.account.id));
      setDialog({
        kind: 'allocate',
        account,
        line: await transactionsApi.get(account.id, line.id),
      });
    } catch {
      onNotify(t('virtualAccount.errorGeneric'));
    }
  }

  async function changed() {
    setTick((n) => n + 1);
    await onChanged();
  }

  const countText =
    totals === null
      ? ''
      : filtered && totals.count !== totals.allCount
        ? t('virtualAccount.ledger.countOf', { shown: totals.count, count: totals.allCount })
        : t('virtualAccount.ledger.count', { count: totals.allCount });

  const cell = (index: number) => ({
    px: '6px',
    ...(index === 0 ? { paddingInlineStart: '20px' } : {}),
    ...(index === COLUMNS.length - 1 ? { paddingInlineEnd: '20px' } : {}),
  });

  return (
    <Paper component="section" aria-labelledby="ledger-title" sx={{ p: 0, overflow: 'hidden' }}>
      <Stack
        direction="row"
        data-card-header=""
        sx={{
          p: '16px 20px 10px',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 1.5,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ minWidth: 0, flex: '1 1 320px' }}>
          <Typography id="ledger-title" variant="h3" component="h2">
            {t('virtualAccount.ledger.title')}
          </Typography>
          <Typography variant="caption" component="p" sx={{ color: tokens.text2 }}>
            {t('virtualAccount.ledger.sub', { name: virtualAccount.name })}
          </Typography>
        </Box>
        <Typography
          variant="caption"
          data-ledger-count=""
          sx={{ color: tokens.textLabel('surface'), whiteSpace: 'nowrap' }}
        >
          {countText}
        </Typography>
      </Stack>
      <Stack
        direction="row"
        sx={{ alignItems: 'center', gap: '10px 16px', flexWrap: 'wrap', p: '0 20px 12px' }}
      >
        <TextField
          size="small"
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('virtualAccount.ledger.search')}
          slotProps={{
            htmlInput: { 'aria-label': t('virtualAccount.ledger.search') },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Box component="span" aria-hidden sx={{ display: 'flex', color: tokens.text3 }}>
                    <SearchIcon />
                  </Box>
                </InputAdornment>
              ),
            },
          }}
          sx={{ flex: '1 1 240px', maxWidth: 340 }}
        />
        <SegmentedControl<LedgerFilter>
          aria-label={t('virtualAccount.ledger.filter.label')}
          filter
          value={filter}
          options={LEDGER_FILTERS.map((value) => ({
            value,
            label: t(`virtualAccount.ledger.filter.${value}`),
          }))}
          onChange={(value) => setUrl({ filter: value })}
        />
        {filter !== 'all' && rows.length > 0 && totals !== null && (
          <Typography
            variant="caption"
            data-ledger-filter-total=""
            sx={{ color: tokens.text2, fontVariantNumeric: 'tabular-nums' }}
          >
            {t(
              filter === 'unassigned'
                ? 'virtualAccount.ledger.totalUnassigned'
                : 'virtualAccount.ledger.totalUncategorized',
              { amount: money(totals.netMinor) },
            )}
          </Typography>
        )}
      </Stack>
      {page !== null && <LiveRegion text={countText} />}

      {failed !== null && (
        <Box sx={{ p: '0 20px 20px' }}>
          <ErrorState
            variant="section"
            error={failed.error}
            what={t('errors.what.virtualAccountRegister')}
            onRetry={() => setTick((n) => n + 1)}
          />
        </Box>
      )}
      {failed === null && page === null && (
        <Box aria-busy="true" sx={{ p: '0 20px 16px' }}>
          {[0, 1, 2, 3].map((n) => (
            <Skeleton key={n} height={40} />
          ))}
        </Box>
      )}
      {page !== null && rows.length > 0 && (
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table
            size="small"
            aria-labelledby="ledger-title"
            sx={{ minWidth: 940, tableLayout: 'fixed' }}
          >
            <colgroup>
              {COLUMNS.map((width, index) => (
                <col key={index} style={width === undefined ? undefined : { width: width + 12 }} />
              ))}
            </colgroup>
            <TableHead>
              <TableRow>
                {(
                  [
                    ['date', 'envelope.colDate'],
                    ['description', 'virtualAccount.ledger.colDescription'],
                    ['category', 'envelope.colCategory'],
                    ['envelope', 'virtualAccount.ledger.colEnvelope'],
                    ['amount', 'envelope.colAmount'],
                    ['balance', 'envelope.colBalance'],
                  ] as const
                ).map(([id, label], index) => (
                  <TableCell key={id} align={index >= 4 ? 'right' : 'left'} sx={cell(index)}>
                    {t(label)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((line) => {
                const parts = line.allocations;
                const categoriesOfLine = lineCategories(parts);
                const account = line.account;
                const activate = (event: MouseEvent<HTMLElement> | KeyboardEvent<HTMLElement>) =>
                  open(line, event.currentTarget);
                return (
                  <TableRow
                    key={line.id}
                    hover
                    tabIndex={0}
                    data-ledger-row={line.id}
                    aria-label={t('virtualAccount.ledger.rowLabel', {
                      description: line.payee,
                      date: formatCivilDate(line.postedOn, locale),
                    })}
                    onClick={activate}
                    onKeyDown={(event) => {
                      if (
                        event.target === event.currentTarget &&
                        (event.key === 'Enter' || event.key === ' ')
                      ) {
                        event.preventDefault();
                        activate(event);
                      }
                    }}
                    sx={{ cursor: 'pointer', verticalAlign: 'middle' }}
                  >
                    <TableCell sx={cell(0)}>
                      <TabularText sx={{ color: tokens.text2, fontSize: 13 }}>
                        {formatCivilDate(line.postedOn, locale, DATE_SHORT)}
                      </TabularText>
                    </TableCell>
                    <TableCell sx={{ ...cell(1), fontSize: 13 }}>
                      <Box sx={{ overflowWrap: 'anywhere' }}>{line.payee}</Box>
                      {account !== null && (
                        <Typography
                          component="div"
                          sx={{
                            fontSize: 11,
                            lineHeight: '16px',
                            color: tokens.textLabel('surface'),
                          }}
                        >
                          {contributionLabel(
                            account.last4 === null
                              ? account.name
                              : t('virtualAccount.ledger.source', {
                                  account: account.name,
                                  last4: account.last4,
                                }),
                            line,
                            account,
                          )}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={cell(2)}>
                      <Stack direction="row" sx={{ gap: 0.5, flexWrap: 'wrap' }}>
                        {categoriesOfLine.map((row) => (
                          <CategoryTag
                            key={row.categoryId ?? 'none'}
                            row={row}
                            name={categoryName(row.categoryId)}
                            showAmount={categoriesOfLine.length > 1}
                            currency={virtualAccount.currency}
                          />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell sx={cell(3)}>
                      <Stack direction="row" sx={{ gap: 0.5, flexWrap: 'wrap' }}>
                        {parts.map((part, index) => (
                          <PartPill
                            key={part.id ?? index}
                            part={part}
                            categoryName={categoryName(part.categoryId)}
                            showAmount={parts.length > 1}
                            currency={virtualAccount.currency}
                          />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell align="right" sx={cell(4)}>
                      <MoneyText
                        minor={line.publishedMinor}
                        currency={virtualAccount.currency}
                        tone="register"
                        variant="body2"
                        signed
                      />
                    </TableCell>
                    <TableCell align="right" sx={cell(5)}>
                      <MoneyText
                        minor={line.runningBalanceMinor}
                        currency={virtualAccount.currency}
                        variant="body2"
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {page !== null && rows.length === 0 && (
        <Typography data-ledger-empty="" sx={{ p: '22px 20px', fontSize: 13, color: tokens.text2 }}>
          {t(filtered ? 'virtualAccount.ledger.emptyFiltered' : 'virtualAccount.ledger.empty')}
        </Typography>
      )}
      {more.failed && (
        <Stack direction="row" sx={{ p: '12px 20px', gap: 1, alignItems: 'center' }}>
          <Typography variant="body2" sx={{ color: tokens.errorText }}>
            {t('virtualAccount.errorGeneric')}
          </Typography>
          <Button size="small" onClick={() => void loadMore()}>
            {t('common.retry')}
          </Button>
        </Stack>
      )}
      {page?.nextCursor && (
        <Stack direction="row" sx={{ justifyContent: 'center', p: '12px 20px 16px' }}>
          <Button variant="outlined" loading={more.busy} onClick={() => void loadMore()}>
            {t('common.loadOlderLines')}
          </Button>
        </Stack>
      )}

      <ObjectMenu
        title={dialog?.kind === 'choice' ? dialog.line.payee : ''}
        anchorEl={dialog?.kind === 'choice' ? dialog.anchor : null}
        open={dialog?.kind === 'choice'}
        onClose={() => setDialog(null)}
      >
        <MenuItem
          onClick={() => {
            if (dialog?.kind === 'choice') void openAllocate(dialog.line);
          }}
        >
          {t('virtualAccount.ledger.changeAllocation')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (dialog?.kind === 'choice') setDialog({ kind: 'split', line: dialog.line });
          }}
        >
          {t('virtualAccount.ledger.resplit')}
        </MenuItem>
      </ObjectMenu>
      {dialog?.kind === 'allocate' && (
        <OwnedEnvelopesProvider>
          <AllocateDialog
            account={dialog.account}
            line={dialog.line}
            editor
            owner
            onClose={() => setDialog(null)}
            onChanged={async (line) => {
              setDialog((prev) => (prev?.kind === 'allocate' ? { ...prev, line } : prev));
              await changed();
            }}
            onNotify={onNotify}
          />
        </OwnedEnvelopesProvider>
      )}
      {dialog?.kind === 'split' && (
        <CategorySplitDialog
          virtualAccount={virtualAccount}
          line={dialog.line}
          envelopes={envelopes}
          categories={categories}
          onClose={() => setDialog(null)}
          onSaved={async () => {
            onNotify(t('virtualAccount.split.saved'));
            await changed();
          }}
          onStale={() => refetchLine(dialog.line.id)}
          onCategoryAdded={async () => {
            await loadCategories();
          }}
          onSubscriptionRequired={onSubscriptionRequired}
        />
      )}
    </Paper>
  );
}
